import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { api } from '../../../../core/utils/apiClient';
import { COLORS } from '../../../../core/constants/colors';

interface HealthStatus {
  status: string;
  database: string;
  aws_s3: string;
  aws_eventbridge: string;
  aws_cognito: string;
  authentication: string;
  document_services: string;
}

export const SystemHealthPage: React.FC = () => {
  const [health, setHealth] = useState<HealthStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadHealthStatus();
  }, []);

  const loadHealthStatus = async () => {
    try {
      setIsLoading(true);
      const response = await api.get('/api/v1/health/infrastructure');
      if (response.ok) {
        const data = await response.json();
        setHealth(data);
      }
    } catch (error) {
      console.error('Error loading health status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusColors: Record<string, { bg: string; text: string }> = {
      healthy: { bg: '#E8F5E9', text: '#2E7D32' },
      degraded: { bg: '#FFF3E0', text: '#E65100' },
      unhealthy: { bg: '#FFEBEE', text: '#C62828' },
      unknown: { bg: '#F5F5F5', text: '#616161' },
    };

    return statusColors[status] || statusColors.unknown;
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div
              className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-t-transparent mb-4"
              style={{ borderColor: COLORS.admin.primary, borderTopColor: 'transparent' }}
            />
            <p style={{ color: '#6B7280' }}>Loading system health...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!health) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-screen">
          <p style={{ color: '#EF4444' }}>Unable to load health status</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout pageTitle="System Health" pageDescription="Monitor the health and status of all system components">
      <div style={{ backgroundColor: '#F5F5F5' }}>
        <div className="max-w-[1400px] mx-auto px-8 py-8">
        {/* Overall Status */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
                <h3 className="text-lg font-bold mb-1" style={{ color: '#1F2937' }}>
                Overall System Status
              </h3>
                <p className="text-sm" style={{ color: '#6B7280' }}>
                Last checked: {new Date().toLocaleString()}
              </p>
            </div>
            <span
              className="px-4 py-2 rounded-full text-sm font-semibold"
              style={{
                backgroundColor: getStatusBadge(health.status).bg,
                color: getStatusBadge(health.status).text,
              }}
            >
              {health.status.toUpperCase()}
            </span>
          </div>
        </div>

        {/* Service Status Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center gap-3">
              <div
                  className="w-12 h-12 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: '#E3F2FD' }}
              >
                <svg className="w-6 h-6" style={{ color: '#1565C0' }} fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="flex-1">
                  <h4 className="font-bold" style={{ color: '#1F2937' }}>
                  Database
                </h4>
                  <p className="text-xs" style={{ color: '#6B7280' }}>
                  PostgreSQL connection status
                </p>
              </div>
              <span
                className="px-3 py-1 rounded-full text-xs font-medium"
                style={{
                  backgroundColor: getStatusBadge(health.database).bg,
                  color: getStatusBadge(health.database).text,
                }}
              >
                {health.database}
              </span>
            </div>
          </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center gap-3">
              <div
                  className="w-12 h-12 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: '#FFF3E0' }}
              >
                <svg className="w-6 h-6" style={{ color: '#E65100' }} fill="currentColor" viewBox="0 0 20 20">
                  <path d="M3 2a1 1 0 000 2h1a2 2 0 012 2v10a2 2 0 01-2 2h-.854a1 1 0 110-.854H6a1 1 0 100-2h10a1 1 0 110 2h-.854a1 1 0 110 .854H14a2 2 0 01-2-2V6a2 2 0 012-2h1a1 1 0 100-2H3z" />
                </svg>
              </div>
              <div className="flex-1">
                  <h4 className="font-bold" style={{ color: '#1F2937' }}>
                  AWS S3
                </h4>
                  <p className="text-xs" style={{ color: '#6B7280' }}>
                  Object storage service
                </p>
              </div>
              <span
                className="px-3 py-1 rounded-full text-xs font-medium"
                style={{
                  backgroundColor: getStatusBadge(health.aws_s3).bg,
                  color: getStatusBadge(health.aws_s3).text,
                }}
              >
                {health.aws_s3}
              </span>
            </div>
          </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center gap-3">
              <div
                  className="w-12 h-12 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: '#E8F5E9' }}
              >
                <svg className="w-6 h-6" style={{ color: '#2E7D32' }} fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 3.5a1.5 1.5 0 013 0V4a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-.5a1.5 1.5 0 000 3h.5a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-.5a1.5 1.5 0 00-3 0v.5a1 1 0 01-1 1H6a1 1 0 01-1-1v-3a1 1 0 00-1-1h-.5a1.5 1.5 0 010-3H4a1 1 0 001-1V6a1 1 0 011-1h3a1 1 0 001-1v-.5z" />
                </svg>
              </div>
              <div className="flex-1">
                  <h4 className="font-bold" style={{ color: '#1F2937' }}>
                  EventBridge
                </h4>
                  <p className="text-xs" style={{ color: '#6B7280' }}>
                  Event-driven architecture
                </p>
              </div>
              <span
                className="px-3 py-1 rounded-full text-xs font-medium"
                style={{
                  backgroundColor: getStatusBadge(health.aws_eventbridge).bg,
                  color: getStatusBadge(health.aws_eventbridge).text,
                }}
              >
                {health.aws_eventbridge}
              </span>
            </div>
          </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center gap-3">
              <div
                  className="w-12 h-12 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: '#E3F2FD' }}
              >
                <svg className="w-6 h-6" style={{ color: '#1565C0' }} fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="flex-1">
                  <h4 className="font-bold" style={{ color: '#1F2937' }}>
                  Cognito
                </h4>
                  <p className="text-xs" style={{ color: '#6B7280' }}>
                  Authentication service
                </p>
              </div>
              <span
                className="px-3 py-1 rounded-full text-xs font-medium"
                style={{
                  backgroundColor: getStatusBadge(health.aws_cognito).bg,
                  color: getStatusBadge(health.aws_cognito).text,
                }}
              >
                {health.aws_cognito}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
    </AdminLayout>
  );
};

